// ── Constants reused by the in-memory data builder and the UI ──

export const STATE_CODES = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'] as const;
export type StateCode = (typeof STATE_CODES)[number];

export const STATE_LABELS: Record<StateCode, string> = {
  NSW: 'New South Wales',
  VIC: 'Victoria',
  QLD: 'Queensland',
  WA: 'Western Australia',
  SA: 'South Australia',
  TAS: 'Tasmania',
  ACT: 'Australian Capital Territory',
  NT: 'Northern Territory',
};

export const WORKER_TYPES = [
  'Train driver',
  'Guard',
  'Controller / signaller',
  'Station staff',
  'Rolling stock maintenance staff',
  'Railway infrastructure maintenance staff',
  'Shunter',
  'Other',
] as const;
export type WorkerType = (typeof WORKER_TYPES)[number];

export const TEST_TYPES = [
  'Alcohol breath test',
  'Alcohol breath analysis',
  'Alcohol blood test',
  'Drug oral fluid',
  'Drug blood test',
  'Drug urine test',
  'Drug test - other',
] as const;
export type TestType = (typeof TEST_TYPES)[number];

export const RESULT_TYPES = ['Positive', 'Negative', 'Refused', 'Tampered/Interfered'] as const;
export type ResultType = (typeof RESULT_TYPES)[number];

export const PERSON_TYPES = ['Employee', 'Contractor'] as const;
export type PersonType = (typeof PERSON_TYPES)[number];

// ── Organisation registers for the Interfaces editor ──
// The "Add organisation" dropdown picks from these master lists. They are
// supersets of the per-period demo data so a brand-new organisation (one that
// was not on last period's return) can still be added.
export const RAIL_INFRASTRUCTURE_MANAGER_OPTIONS: string[] = [
  'ARTC',
  'Arc Infrastructure',
  'Metro Trains Melbourne',
  'Operator Rail Pty Ltd',
  'Queensland Rail',
  'Rio Tinto Rail',
  'Sydney Trains',
  'V/Line Corporation',
];

export const ROLLING_STOCK_OPERATOR_OPTIONS: string[] = [
  '3801 LTD',
  'Aurizon Bulk Central Pty Ltd',
  'Aurizon Operations Limited',
  'Brimble Rail Pty Ltd',
  'Great Southern Rail Pty Ltd',
  'Holdco Holdings Pty Ltd',
  'John Holland Pty Ltd',
  'Journey Beyond Rail Pty Ltd',
  'Lachlan Valley Railway Society Co-operative Ltd',
  "Laing O'Rourke Australia Construction Pty Ltd",
  'Martinus Rail Pty Ltd',
  'NSW Trains',
  'One Rail Australia (FLA) Pty Ltd',
  'Pacific National Pty Ltd',
  'Qube Holdings Limited',
  'SCT Logistics',
  'Southern Shorthaul Railroad Pty Ltd',
  'Watco Australia Pty Ltd',
];

// ── Domain interfaces ──

export type MonthlyReturnStatus = 'Not Started' | 'In Progress' | 'Submitted' | 'Overdue';

export interface InterfacesData {
  railInfrastructureManagers: string[];
  rollingStockOperators: string[];
  previousRailInfrastructureManagers: string[];
  previousRollingStockOperators: string[];
  previousPeriodLabel: string;
}

export interface GeneralData {
  totalNumber: number | null;
  totalHours: number | null;
  selectedMode: 'Total number' | 'Total hours';
}

export interface MovementData {
  // Rail infrastructure manager
  passengerTrainKilometres: number | null;
  freightTrainKilometres: number | null;
  // Rolling stock operator
  passengerTrainKm: number | null;
  freightTrainKm: number | null;
  maintenanceTrainKm: number | null;
  passengerJourneys: number | null;
}

/** Counts for one result column (Positive / Negative / Refused / Tampered) */
export interface ResultCounts {
  employee: number | null;
  contractor: number | null;
}

/** Counts for one test row split between pre-sign-on and post-sign-on. */
export interface TestRowCounts {
  preSignOn: Record<ResultType, ResultCounts>;
  postSignOn: Record<ResultType, ResultCounts>;
}

export interface DaTestRow {
  testType: TestType;
  counts: TestRowCounts;
}

export interface DaWorkerSection {
  workerType: WorkerType;
  rows: DaTestRow[];
}

export type DaTestingByWorkerType = DaWorkerSection[];

/** Per-state data for the state-scoped sections. */
export interface StateData {
  state: StateCode;
  general: GeneralData;
  movement: MovementData;
  daTesting: DaTestingByWorkerType;
}

export interface MonthlyReturn {
  id: number;
  organisationName: string;
  reportingMonth: string; // e.g. "November 2025"
  monthIndex: number; // 1..12
  year: number;
  dueDate: string; // dd/mm/yyyy
  submittedDate: string | null;
  status: MonthlyReturnStatus;
  states: StateCode[]; // states active for this organisation
  interfaces: InterfacesData;
  perState: StateData[];
}

// ── Helpers ──

export function buildEmptyResultCounts(): Record<ResultType, ResultCounts> {
  return {
    Positive: { employee: null, contractor: null },
    Negative: { employee: null, contractor: null },
    Refused: { employee: null, contractor: null },
    'Tampered/Interfered': { employee: null, contractor: null },
  };
}

export function buildEmptyTestRow(testType: TestType): DaTestRow {
  return {
    testType,
    counts: {
      preSignOn: buildEmptyResultCounts(),
      postSignOn: buildEmptyResultCounts(),
    },
  };
}

export function buildEmptyWorkerSection(workerType: WorkerType): DaWorkerSection {
  return {
    workerType,
    rows: TEST_TYPES.map(buildEmptyTestRow),
  };
}

export function buildEmptyDaTesting(): DaTestingByWorkerType {
  return WORKER_TYPES.map(buildEmptyWorkerSection);
}

export function buildEmptyStateData(state: StateCode): StateData {
  return {
    state,
    general: { totalNumber: null, totalHours: null, selectedMode: 'Total number' },
    movement: {
      passengerTrainKilometres: null,
      freightTrainKilometres: null,
      passengerTrainKm: null,
      freightTrainKm: null,
      maintenanceTrainKm: null,
      passengerJourneys: null,
    },
    daTesting: buildEmptyDaTesting(),
  };
}
